import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, vShow as _vShow, withDirectives as _withDirectives, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-5d5e47e2"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["id"]
const _hoisted_2 = {
  class: "modal-box",
  method: "dialog"
}
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("hr", null, null, -1))
const _hoisted_4 = { class: "grid grid-cols-2 grid-rows-1 gap-6" }
const _hoisted_5 = { class: "modal-action" }

import { ref, onMounted, watch, toRef } from "vue";
import {
    createSupplier,
    getSupplier,
    updateSupplier,
} from "@/_services/supplier/supplier";
import { createZodPlugin } from "@formkit/zod";
import { z } from "zod";
import swal from "@/_helpers/swal";
import { ISupplier } from "@/models/supplier/ISupplier";

export default /*#__PURE__*/_defineComponent({
  __name: 'SupplierFormModal',
  props: {
    supplierUuid: {
        type: String,
        required: false,
        default: "",
    },
    supplierFormModalId: {
        type: String,
        required: true,
    },
},
  setup(__props) {

const props = __props;

const isFee = ref("0");
const loaded = ref(false);
const showMontant = ref(true);
const supplier = ref<ISupplier>({
    name: "",
    email: "",
    phoneNumber: "",
    notes: "",
    fees: 0,
} as unknown as ISupplier);

const supplierFormModalId = toRef(props, "supplierFormModalId");
const modalHTMLElement = ref<HTMLDialogElement | null>(null);
const supplierUuid = toRef(props, "supplierUuid");
const modalDialogState = ref<"open" | "close">("close");

const observer = new MutationObserver((mutationsList) => {
    for (const mutation of mutationsList) {
        if (mutation.attributeName === "open" && modalHTMLElement.value) {
            const isOpen = modalHTMLElement.value.open;
            const isclosed = !isOpen;
            modalDialogState.value = isOpen ? "open" : "close";
            if (supplierUuid.value && isclosed) {
                updateSupplierFromApi(supplierUuid.value as string);
            }
            if (
                (!supplierUuid.value && isclosed) ||
                (!supplierUuid.value && isOpen)
            ) {
                resetSupplierData();
            }

            break; // Exit the loop once the class attribute has been processed
        }
    }
});

onMounted(() => {
    modalHTMLElement.value = document.querySelector(
        `#${supplierFormModalId.value}`
    );
    if (modalHTMLElement.value) {
        observer.observe(modalHTMLElement.value, { attributes: true });
    }
});

const modalTitle = props.supplierUuid
    ? ref<string>("Modifier un fournisseur")
    : ref<string>("Créer un fournisseur");

const savedFee = ref<number>(0);

const triggerFee = () => {
    if (isFee.value == "0") {
        savedFee.value = supplier.value.fees as number;
        supplier.value.fees = 0;
    } else {
        supplier.value.fees = savedFee.value;
    }
};

const zodSchema = z.object({
    name: z.string()
        .nonempty("Le champs Nom du fournisseur est obligatoire")
        .min(1, { message: "Le champs Nom du fournisseur est obligatoire" })
        .max(50, { message: "Le nom du fournisseur ne doit pas dépasser 50 caractères" }),
    fees: z.coerce
        .number()
        .nullable()
        .refine((data) => validateFee(data), { message: "Le montant est invalide" }),
    notes: z.string()
        .max(255, { message: "La note ne doit pas dépasser 255 caractères" })
        .nullable(),
    email: z.string()
        .max(255, { message: "L'email ne doit pas dépasser 255 caractères" })
        .nullable(),
    phoneNumber: z.string()
        .max(255, { message: "Le numéro de téléphone ne doit pas dépasser 255 caractères" })
        .nullable(),
});

const decimalFeesRegex = new RegExp(/^\d{1,3}(\.\d{1,2})?$/);
const validateFee = (data) => {
    return !(
        isFee.value == "1" &&
        supplier.value.fees != null &&
        supplier.value.fees >= 0 &&
        false === decimalFeesRegex.test(data as string)
    );
};

const [zodPlugin, submitHandler] = createZodPlugin(
    zodSchema,
    async (formData) => {
        try {
            closeModalDialog();
            const { name, fees, notes, email, phoneNumber } = formData;
            const data = {
                name,
                fees: fees && isFee.value == "1" ? fees.toString() : "0",
                email: typeof email === "string" ? email.trim() : null,
                notes: typeof notes === "string" ? notes : null,
                phoneNumber: null === phoneNumber ? null : phoneNumber.trim()
            };
            const isEdit = Boolean(props.supplierUuid);
            const title = isEdit
                ? "Êtes-vous sûr de vouloir éditer le fournisseur"
                : "Êtes-vous sûr de vouloir créer le fournisseur";
            const confirmText = isEdit ? "Oui, éditer" : "Oui, créer";

            await swal
                .confirm(title, "Annuler", confirmText)
                .then((results) => results.isConfirmed && createOrUpdate(data));
        } catch (err: any) {
            swal.error(err.errors[0]);
        }
    }
)

const createOrUpdate = (data) => {
    props.supplierUuid
        ? updateSupplier(props.supplierUuid as string, data)
        : createSupplier(data);
    closeModalDialog();
};

const closeModalDialog = () => {
    if (modalHTMLElement.value) {
        modalHTMLElement.value.close();
    }
};

const resetSupplierData = () => {
    supplier.value.name = "";
    supplier.value.email = "";
    supplier.value.phoneNumber = "";
    supplier.value.notes = "";
    supplier.value.fees = 0;
    loaded.value = true;
    isFee.value = "0";
};

watch(
    () => props.supplierUuid,
    (uuid) => {
        if (uuid) {
            modalTitle.value = "Modifier un fournisseur";
            updateSupplierFromApi(uuid as string);
        } else {
            modalTitle.value = "Créer un fournisseur";
        }
    }
);
watch(
    isFee,
    (value) => {
        showMontant.value = value == "1";
    },
    { immediate: true }
);

const updateSupplierFromApi = (uuid: string) => {
    getSupplier(uuid).then((res) => {
        supplier.value = res;
        supplier.value.phoneNumber = supplier.value.phoneNumber
            ? supplier.value.phoneNumber
            : "";
        isFee.value =
            supplier.value.fees != null && supplier.value.fees != 0 ? "1" : "0";
        supplier.value.fees = supplier.value.fees != null ? supplier.value.fees : 0;
        loaded.value = true;
    });
};

return (_ctx: any,_cache: any) => {
  const _component_FormKit = _resolveComponent("FormKit")!

  return (_openBlock(), _createElementBlock("dialog", {
    ref: "HTMLDialogElement",
    id: supplierFormModalId.value,
    class: "modal modal-middle sm:modal-middle"
  }, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_component_FormKit, {
        type: "form",
        id: "supplierForm",
        onSubmit: _unref(submitHandler),
        plugins: [_unref(zodPlugin)],
        actions: false,
        "incomplete-message": "Désolé, tous les champs ne sont pas remplis correctement."
      }, {
        default: _withCtx(({ state: { valid } }) => [
          _createElementVNode("h2", null, _toDisplayString(_unref(modalTitle)), 1),
          _hoisted_3,
          _createVNode(_component_FormKit, {
            type: "radio",
            modelValue: isFee.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((isFee).value = $event)),
            label: "Frais de gestion :",
            name: "isFee",
            options: [
                        { label: 'Oui', value: '1' },
                        { label: 'Non', value: '0' },
                    ],
            onSelect: _cache[1] || (_cache[1] = ($event: any) => (triggerFee()))
          }, null, 8, ["modelValue"]),
          _withDirectives(_createVNode(_component_FormKit, {
            type: "number",
            modelValue: supplier.value.fees,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((supplier.value.fees) = $event)),
            name: "fees",
            label: "Montant",
            step: "any",
            help: "un nombre entier ou si décimal, il faut 1 à 3 chiffres avant le (.) ou la (,)\n                        et 1 à 2 chiffres après le (.) ou la (,)"
          }, null, 8, ["modelValue"]), [
            [_vShow, showMontant.value]
          ]),
          _createVNode(_component_FormKit, {
            modelValue: supplier.value.name,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((supplier.value.name) = $event)),
            type: "text",
            name: "name",
            label: "Nom du fournisseur"
          }, null, 8, ["modelValue"]),
          _createElementVNode("div", _hoisted_4, [
            _createVNode(_component_FormKit, {
              modelValue: supplier.value.email,
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((supplier.value.email) = $event)),
              type: "text",
              name: "email",
              label: "Email"
            }, null, 8, ["modelValue"]),
            _createVNode(_component_FormKit, {
              type: "text",
              modelValue: supplier.value.phoneNumber,
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((supplier.value.phoneNumber) = $event)),
              name: "phoneNumber",
              label: "Téléphone"
            }, null, 8, ["modelValue"])
          ]),
          _createVNode(_component_FormKit, {
            type: "textarea",
            modelValue: supplier.value.notes,
            "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((supplier.value.notes) = $event)),
            name: "notes",
            label: "Note"
          }, null, 8, ["modelValue"]),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("button", {
              type: "button",
              class: "btn",
              onClick: _cache[7] || (_cache[7] = ($event: any) => (closeModalDialog()))
            }, "Annuler"),
            _createVNode(_component_FormKit, {
              type: "submit",
              label: props.supplierUuid ? 'Modifier' : 'Créer',
              disabled: !valid
            }, null, 8, ["label", "disabled"])
          ])
        ]),
        _: 1
      }, 8, ["onSubmit", "plugins"])
    ])
  ], 8, _hoisted_1))
}
}

})